import { useEffect, useState } from 'react';
import { Building2, Home } from 'lucide-react';
import type { Organization } from '../../../types/admin';

export interface MoveTypesData {
  commercialEnabled: boolean;
  residentialEnabled: boolean;
}

interface MoveTypesStepProps {
  org: Organization;
  onChange: (data: MoveTypesData) => void;
}

export function MoveTypesStep({ org, onChange }: MoveTypesStepProps) {
  const [commercial, setCommercial] = useState(org.featureCommercialMovingQuotes !== false);
  const [residential, setResidential] = useState(org.featureResidentialMovingQuotes === true);
  const [error, setError] = useState('');

  useEffect(() => {
    setError('');
    if (!commercial && !residential) {
      setError('At least one move type must be enabled.');
    }
    onChange({ commercialEnabled: commercial, residentialEnabled: residential });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commercial, residential]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate">
        Select the types of moves your company handles. You can change this anytime in Settings.
      </p>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-600">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Commercial */}
        <button
          type="button"
          onClick={() => setCommercial(!commercial)}
          className={`relative flex flex-col items-center gap-3 p-5 rounded-xl transition-all text-left ${
            commercial
              ? 'border-[3px] border-plum bg-plum/5 shadow-brand-md'
              : 'border-2 border-stone bg-white hover:border-plum/40'
          }`}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            commercial ? 'bg-plum/10 text-plum' : 'bg-mist text-slate'
          }`}>
            <Building2 className="w-6 h-6" />
          </div>
          <div className="text-center">
            <p className={`text-sm font-semibold ${commercial ? 'text-midnight' : 'text-slate'}`}>
              Commercial Moves
            </p>
            <p className="text-xs text-slate mt-1">
              Office relocations, commercial spaces, warehouses
            </p>
          </div>
          {/* Toggle indicator */}
          <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
            commercial ? 'border-plum bg-plum' : 'border-stone'
          }`}>
            {commercial && <span className="text-white text-xs font-bold">✓</span>}
          </div>
        </button>

        {/* Residential */}
        <button
          type="button"
          onClick={() => setResidential(!residential)}
          className={`relative flex flex-col items-center gap-3 p-5 rounded-xl transition-all text-left ${
            residential
              ? 'border-[3px] border-plum bg-plum/5 shadow-brand-md'
              : 'border-2 border-stone bg-white hover:border-plum/40'
          }`}
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            residential ? 'bg-plum/10 text-plum' : 'bg-mist text-slate'
          }`}>
            <Home className="w-6 h-6" />
          </div>
          <div className="text-center">
            <p className={`text-sm font-semibold ${residential ? 'text-midnight' : 'text-slate'}`}>
              Residential Moves
            </p>
            <p className="text-xs text-slate mt-1">
              Household moves, apartments, single-family homes
            </p>
          </div>
          <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
            residential ? 'border-plum bg-plum' : 'border-stone'
          }`}>
            {residential && <span className="text-white text-xs font-bold">✓</span>}
          </div>
        </button>
      </div>
    </div>
  );
}
